import { Button, IconButton } from "../../../design-system";
import React from "react";
import {
  HiOutlineChevronLeft,
  HiOutlinePause,
  HiOutlinePlay,
} from "react-icons/hi2";
import { MdSkipNext, MdSkipPrevious } from "react-icons/md";
import { AlbumData } from "../Playlist.types";

type AlbumPanelProps = {
  variant: "desktop" | "mobile";
  albumData: AlbumData;
  mediaContent?: React.ReactNode;
  tracksCount: number;
  totalDurationMinutes: number;
  isPlaying: boolean;
  primaryTrackTitle?: string;
  onPlayPause: () => void;
  onPreviousTrack: () => void;
  onNextTrack: () => void;
  onBackToCatalog: () => void;
  onAction: () => void;
};

function AlbumPanel({
  variant,
  albumData,
  mediaContent,
  tracksCount,
  totalDurationMinutes,
  isPlaying,
  primaryTrackTitle,
  onPlayPause,
  onPreviousTrack,
  onNextTrack,
  onBackToCatalog,
  onAction,
}: AlbumPanelProps) {
  if (variant === "desktop") {
    return (
      <section className="w-[280px] shrink-0 text-center">
        <h1 className="text-3xl font-bold leading-tight">{albumData.title}</h1>
        <div className="w-full mt-4 rounded-lg overflow-hidden">
          {mediaContent || (
            <img
              src={albumData.coverUrl || "images/album_over_top.jpg"}
              alt={albumData.title}
              className="w-full rounded-lg"
              onError={(event) => {
                event.currentTarget.src = "images/album_inner.jpg";
              }}
            />
          )}
        </div>

        <h2 className="text-5xl font-semibold mt-5 leading-tight">
          {albumData.shortTitle || albumData.title}
        </h2>
        <p className="text-player-muted mt-3">Álbum • {albumData.year || "-"}</p>
        <p className="text-player-muted">
          {tracksCount || albumData.totalTracks || 0} músicas •{" "}
          {totalDurationMinutes} minutos
        </p>

        <div className="mt-6 flex justify-center gap-3">
          <IconButton label="Previous track"
            onClick={onPreviousTrack}
            tone="raised" size="sm"
          >
            <MdSkipPrevious size={24} />
          </IconButton>
          <IconButton label={isPlaying ? "Pause" : "Play"}
            onClick={onPlayPause}
            tone="inverted" size="lg"
          >
            {isPlaying ? (
              <HiOutlinePause size={35} />
            ) : (
              <HiOutlinePlay size={35} />
            )}
          </IconButton>
          <IconButton label="Next track"
            onClick={onNextTrack}
            tone="raised" size="sm"
          >
            <MdSkipNext size={24} />
          </IconButton>
        </div>
      </section>
    );
  }

  return (
    <>
      <header className="flex items-center justify-between py-3">
        <IconButton label="Back to albums" onClick={onBackToCatalog}>
          <HiOutlineChevronLeft size={28} />
        </IconButton>
        <div className="text-center">
          <p className="text-player-secondary text-sm">
            {albumData.shortTitle || albumData.title}
          </p>
          <p className="text-player-secondary font-semibold text-lg">
            Álbum • {albumData.year || "-"}
          </p>
        </div>
        <span className="w-7" />
      </header>

      <section className="text-center">
        <div className="w-full rounded-xl overflow-hidden">
          {mediaContent || (
            <img
              src={albumData.coverUrl || "images/album_over_top.jpg"}
              alt={albumData.title}
              className="w-full rounded-xl"
              onError={(event) => {
                event.currentTarget.src = "images/album_inner.jpg";
              }}
            />
          )}
        </div>
        <h2 className="text-5xl leading-tight font-bold mt-4">
          {albumData.title}
        </h2>

        <div className="mt-5 flex justify-between px-2">
          <IconButton label="Previous track"
            onClick={onPreviousTrack}
            tone="surface" size="md"
          >
            <MdSkipPrevious size={28} />
          </IconButton>

          <IconButton label={isPlaying ? "Pause" : "Play"}
            onClick={onPlayPause}
            tone="muted" size="md"
          >
            {isPlaying ? (
              <HiOutlinePause size={28} />
            ) : (
              <HiOutlinePlay size={28} />
            )}
          </IconButton>
          <IconButton label="Next track"
            onClick={onNextTrack}
            tone="surface" size="md"
          >
            <MdSkipNext size={28} />
          </IconButton>
        </div>
      </section>

      <section className="mt-6 rounded-2xl bg-player-surface px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-player-secondary">Retomar álbum</p>
          <p className="text-player-text text-2xl leading-tight">
            {primaryTrackTitle
              ? `1. ${primaryTrackTitle}`
              : "Sem música disponível"}
          </p>
        </div>
        <Button variant="ghost" size="none"
          onClick={onPlayPause}
          className="rounded-full bg-white text-black px-5 py-2 text-2xl font-semibold"
        >
          {isPlaying ? "Pausar" : "Retomar"}
        </Button>
      </section>
    </>
  );
}

export default AlbumPanel;
